import { defineStore } from 'pinia';
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';

import type { IAppConfigResponse } from '@modules/config/types';
import type { ISessionResponse } from '@modules/tokens/types';
import {
  ADMIN_AFFILIATES_APPROVED_ROUTE_NAME,
} from '@modules/affiliates-approved/constants';
import { useAffiliateSupervisionMutation } from '@modules/affiliates-approved/mutations';
import { useConfig } from '@modules/config/composables';
import { CONFIG_QUERY_KEY } from '@modules/config/constants';
import { AFFILIATE_DASHBOARD_ROUTE_NAME } from '@modules/dashboard/constants';
import { useI18nStore } from '@modules/i18n/store';
import { PERMISSIONS_QUERY_KEY } from '@modules/permissions/constants';
import { useNamespaceStore } from '@modules/permissions/store';
import { TOKENS_QUERY_KEY } from '@modules/tokens/constants';
import { useTokensQuery } from '@modules/tokens/queries';
import { useUserSignOutMutation } from '@modules/user/mutations';
import * as Sentry from '@sentry/vue';
import { useQueryClient } from '@tanstack/vue-query';
import { nextAnimationFrame } from '@utils/browser';
import { isNumber } from '@utils/guards';

import type { IUser } from '../types';

const useUserStore = defineStore('user', () => {
  const { config } = useConfig();
  const isSupervisionSignOutProcess = ref(false);
  const { setNamespace } = useNamespaceStore();
  const { setLocale } = useI18nStore();
  const user = computed<Maybe<IUser>>(() => config.value?.current_user);
  const isAuthorised = computed(() => !!user.value?.id);
  const { mutateAsync: signOutUser, isLoading: isSignOutLoading } = useUserSignOutMutation();
  const { data: token } = useTokensQuery();
  const {
    mutateAsync: openSupervision,
  } = useAffiliateSupervisionMutation();
  const router = useRouter();
  const queryClient = useQueryClient();

  watch(user, (newValue) => {
    setNamespace(newValue?.namespace);
    Sentry.setUser(newValue ? { id: newValue?.id, username: newValue?.namespace } : null);
  }, { immediate: true, deep: true });

  async function signOut(): Promise<void> {
    if (isAuthorised.value) {
      const isSupervisionSignOut = !!token.value?.supervision;
      isSupervisionSignOutProcess.value = isSupervisionSignOut;

      await signOutUser(isSupervisionSignOut
        ? {
            supervision: token.value?.supervision,
          }
        : {});

      if (isSupervisionSignOut) {
        await router.push({
          name: ADMIN_AFFILIATES_APPROVED_ROUTE_NAME,
        });
        isSupervisionSignOutProcess.value = false;
      } else {
        isSupervisionSignOutProcess.value = false;
      }
    }
  }

  async function goToSupervision(currentUserOrId: IUser | number): Promise<void> {
    let userToSet: IUser;
    if (isNumber(currentUserOrId)) {
      const response = await openSupervision(currentUserOrId);
      userToSet = response.current_user;
      if (response.current_user?.locale) {
        await setLocale(response.current_user.locale);
      }
    } else {
      userToSet = currentUserOrId;
    }

    await queryClient.invalidateQueries({ queryKey: [PERMISSIONS_QUERY_KEY] });

    queryClient.setQueryData<IAppConfigResponse>([CONFIG_QUERY_KEY], (data) => {
      if (!data) {
        return data;
      }

      return {
        ...data,
        current_user: userToSet,
      };
    });

    queryClient.setQueryData<ISessionResponse>([TOKENS_QUERY_KEY], (data) => {
      if (!data) {
        return data;
      }

      return {
        ...data,
        supervision: 1,
      };
    });

    await nextTick();
    await nextAnimationFrame();
    void router.push({
      name: AFFILIATE_DASHBOARD_ROUTE_NAME,
    });
  }

  return {
    user,
    isAuthorised,
    isSupervisionSignOutProcess,
    isSignOutLoading,
    signOut,
    goToSupervision,
  };
});

export default useUserStore;
